import type { ApolloError } from '@apollo/client';

import { GraphQLErrorMessages } from '@fhs-legacy/frontend/src/enums/graphql';
import { DeliveryStatus } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import type { IGetClosestAvailableDeliveryRestaurantResult } from '@fhs-legacy/frontend/src/remote/api/restaurants';
import type { IDeliveryStoreAssignedAttributes } from '@fhs-legacy/frontend/src/state/crm-events/types';

import {
  DeliveryAssignedRestaurant,
  DeliveryQuoteOutcome,
  DeliveryQuoteOutcomeType,
  InvalidPhoneNumberAlert,
} from './types';

export const DELIVERY_CLOSED_TITLE = 'Delivery Restaurants Closed';
export const DELIVERY_CLOSED_MESSAGE =
  'The restaurants that deliver to this address are closed right now. Try again later or switch to pickup.';
export const QUOTE_ERROR_MESSAGE = 'Please try again later.';
export const INVALID_PHONE_NUMBER_ALERT: InvalidPhoneNumberAlert = {
  body: 'We are unable to confirm your phone number. Please confirm your phone number is correct or try a different one.',
  buttonLabel: 'Add Phone Number',
  heading: 'Please update your account phone number to continue',
};

/** What the guest should see for a delivery quote. */
export function getDeliveryQuoteOutcome({
  store,
  deliveryQuote,
  storeStatus,
}: IGetClosestAvailableDeliveryRestaurantResult): DeliveryQuoteOutcome {
  // A restaurant alone is not enough: one also comes back with a failed quote.
  if (store && deliveryQuote === DeliveryStatus.QUOTE_SUCCESSFUL) {
    return { type: DeliveryQuoteOutcomeType.SUCCESS, store };
  }

  if (deliveryQuote === DeliveryStatus.QUOTE_UNAVAILABLE) {
    return { type: DeliveryQuoteOutcomeType.UNAVAILABLE };
  }

  if (deliveryQuote === DeliveryStatus.QUOTE_ERROR) {
    return { type: DeliveryQuoteOutcomeType.ERROR };
  }

  return { type: DeliveryQuoteOutcomeType.STORE_STATUS, storeStatus };
}

export const hasInvalidPhoneNumberError = (error?: ApolloError) =>
  Boolean(
    error?.graphQLErrors?.some(
      ({ message }) => message === GraphQLErrorMessages.INVALID_PHONE_NUMBER
    )
  );

export const getDeliveryStoreAssignedAttributes = (
  restaurant: DeliveryAssignedRestaurant,
  quotedFeeCents: number
): IDeliveryStoreAssignedAttributes => ({
  restaurantId: restaurant._id || '',
  storeId: restaurant.storeId || '',
  restaurantAddress: restaurant.physicalAddress?.address1 || '',
  restaurantZip: restaurant.physicalAddress?.postalCode || '',
  restaurantCity: restaurant.physicalAddress?.city || '',
  restaurantState: restaurant.physicalAddress?.stateProvince || '',
  restaurantCountry: restaurant.physicalAddress?.country || '',
  quotedFeeCents,
});
